// Shared x-axis logic for the inline bar charts (MiniBars / GroupedBars).
//
// The hard part is labelling a dense axis without lying or colliding. A day-bucket window that
// spans Jun 10 -> Jul 10 renders "10 ... 10", which is ambiguous. So: every month boundary (or
// year boundary, when bucketing by month) gets a *forced, qualified* tick, and the periodic
// in-between ticks stay bare numbers. Labels are returned as stacked lines:
//
//     10        15        1         5
//     Jun                 Jul
//     2026
//
// Periodic ticks adjacent to a boundary are dropped so the labels never overlap.

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Bars flex to fill the card. MIN keeps them legible (and triggers horizontal scrolling once
// there are too many to fit); MAX stops a handful of bars from becoming absurdly fat.
export const BAR_MIN = { day: 8, week: 14, month: 22, year: 26 };
export const BAR_MAX = { day: 26, week: 40, month: 64, year: 80 };

/**
 * Cross-axis alignment for a tick label. Labels are wider than their bar and overflow evenly
 * when centred, so the first and last ticks anchor inward — otherwise the scroll container
 * clips them ("3 Jun 2025" losing its leading characters).
 */
export function tickAlign(i, count) {
  if (i === 0) return 'flex-start';
  if (i === count - 1) return 'flex-end';
  return 'center';
}

/** Flex sizing shared by a chart's bar row and its tick row so the two stay aligned. */
export function barFlex(bucket, scale = 1) {
  return {
    flex: '1 1 0',
    minWidth: (BAR_MIN[bucket] || BAR_MIN.day) * scale,
    maxWidth: (BAR_MAX[bucket] || BAR_MAX.day) * scale,
  };
}

/** Parse 'YYYY-MM-DD' without timezone shifting (new Date('YYYY-MM-DD') parses as UTC). */
export function parts(iso) {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return { y, m, d };
}

/** Unambiguous label for hover tooltips. */
export function fullLabel(iso, bucket) {
  const { y, m } = parts(iso);
  if (!y) return String(iso || '');
  if (bucket === 'year') return String(y);
  if (bucket === 'month') return `${MONTHS[m - 1]} ${y}`;
  if (bucket === 'week') return `Week of ${String(iso).slice(0, 10)}`;
  return String(iso).slice(0, 10);
}

/** How often to print a bare tick, so a dense axis stays readable. */
function tickEvery(bucket, count) {
  if (bucket === 'day') return count > 20 ? 5 : (count > 10 ? 2 : 1);
  if (bucket === 'week') return count > 14 ? 4 : 2;
  if (bucket === 'year') return count > 12 ? 2 : 1;
  return 1; // month: all 12 fit
}

// Labels are returned as lines, stacked under the bar:  10
//                                                       Jun
//                                                       2026
function bare(iso, bucket) {
  const { y, m, d } = parts(iso);
  if (bucket === 'year') return [String(y)];
  if (bucket === 'month') return [MONTHS[m - 1]];
  if (bucket === 'week') return [String(d), MONTHS[m - 1]];
  return [String(d)];
}

function qualified(iso, bucket, yearChanged) {
  const { y, m, d } = parts(iso);
  if (bucket === 'year') return [String(y)];
  if (bucket === 'month') return [MONTHS[m - 1], String(y)];
  // day / week: name the month, and the year too when it just rolled over
  return yearChanged ? [String(d), MONTHS[m - 1], String(y)] : [String(d), MONTHS[m - 1]];
}

/**
 * Returns an array parallel to `dates`: a label string, or null where no tick should print.
 * Boundary ticks (new month, or new year when bucketing by month/year) are always printed.
 */
export function buildTicks(dates, bucket) {
  const n = (dates || []).length;
  if (n === 0) return [];
  const info = dates.map(parts);
  const every = tickEvery(bucket, n);
  const last = n - 1;

  const yearChangedAt = (i) => i === 0 || info[i].y !== info[i - 1].y;
  const isBoundary = (i) => {
    if (i === 0) return true;
    if (bucket === 'year') return false;          // every bar is already a year
    if (bucket === 'month') return yearChangedAt(i);
    return info[i].m !== info[i - 1].m;           // day / week: new month
  };

  const ticks = new Array(n).fill(null);
  const boundaries = new Set();
  for (let i = 0; i < n; i += 1) {
    if (isBoundary(i)) {
      boundaries.add(i);
      ticks[i] = qualified(dates[i], bucket, yearChangedAt(i));
    }
  }
  // Bare periodic ticks, anchored to the newest bucket so the last bar is always labelled.
  // A stacked label is wider than a day/week bar, so drop any periodic tick sitting right next
  // to a forced boundary tick (otherwise "30" collides with "1 Jul"). Month/year bars are wide
  // enough to keep every tick.
  const crowds = bucket === 'day' || bucket === 'week';
  for (let i = last; i >= 0; i -= 1) {
    if (ticks[i] || (last - i) % every !== 0) continue;
    if (crowds && (boundaries.has(i - 1) || boundaries.has(i + 1))) continue;
    ticks[i] = bare(dates[i], bucket);
  }
  return ticks;
}
